import type { Scalars } from '@generated/graphql'

import storeConfig from '../../store.config'

const { account } = storeConfig

export const normalize = (text: string) =>
  text
    ?.toLowerCase()
    ?.replaceAll(' ', '_')
    ?.normalize('NFD')
    ?.replace(/\W/g, '')

export const isFallback = (
  recommendations: RestrucuredRecommendation[],
  responseFixRecommendationsStructure: RestrucuredRecommendation[]
) => {
  let fallbackBollean = false

  if (recommendations?.length && !responseFixRecommendationsStructure?.length) {
    fallbackBollean = true
  }

  return fallbackBollean
}

const sortRecommendations = (
  restructuredRecommendations: RestrucuredRecommendation[]
) => {
  const sortedRecommendations = [...restructuredRecommendations]?.sort(
    (recommendationA, recommendationB) =>
      recommendationB?.keys - recommendationA?.keys
  )

  sortedRecommendations?.unshift(
    sortedRecommendations?.splice(
      sortedRecommendations?.findIndex(
        (el) => el?.keys === sortedRecommendations?.[0]?.keys
      ),
      1
    )?.[0]
  )

  return sortedRecommendations
}

export const fallback = (
  recommendations: ProductRecommendation[],
  productSelected: Product
) => {
  const finalRecommendationsFallback: FinalRecommendationsFallback = {
    similar: {},
  }

  const objectProductSelected = {
    name: productSelected?.ProductName,
    link: productSelected?.DetailUrl,
    imgLink: productSelected?.Images?.[0]?.ImageUrl,
    selected: true,
    type: 'fallback',
    value: productSelected?.ProductName,
  }

  finalRecommendationsFallback.similar[
    normalize(`selected_${productSelected?.ProductName}`)
  ] = objectProductSelected

  for (const product of recommendations) {
    const objectProductFallback = {
      name: product?.productName,
      link: new URL(product?.link).pathname,
      imgLink: product.items?.[0]?.images?.[0]?.imageUrl,
      selected: false,
      type: 'fallback',
      value: product?.productName,
    }

    finalRecommendationsFallback.similar[normalize(product?.productName)] =
      objectProductFallback
  }

  return finalRecommendationsFallback
}

export const selectedProduct = (productSelected: Product) => {
  const groupSimilaridade = productSelected?.ProductSpecifications?.filter(
    (product) => normalize(product.FieldGroupName) === 'similaridade'
  )

  const restructuredRecommendations: Array<
    Omit<RestrucuredRecommendation, 'keys'>
  > = []

  const similarity: ObjStandard = {}

  groupSimilaridade?.forEach((item) => {
    const lastUnderscoreIndex = item?.FieldName?.lastIndexOf('_')
    const name = item?.FieldName?.slice(lastUnderscoreIndex + 1)
    const key = item?.FieldName?.slice(0, lastUnderscoreIndex)

    if (!similarity[normalize(key)]) {
      similarity[normalize(key)] = {}
    }

    if (name?.toLowerCase() === 'valor') {
      similarity[normalize(key)] = {
        ...similarity[normalize(key)],
        originalValue: item?.FieldValues?.[0],
      }
    }

    similarity[normalize(key)][normalize(name)] = normalize(
      item?.FieldValues?.[0]
    )
  })

  const finalProduct = {
    name: productSelected?.ProductName,
    link: productSelected?.DetailUrl,
    similarity,
  }

  restructuredRecommendations.push(finalProduct)

  return restructuredRecommendations
}

const sortingByPriority = (
  similarityGroup: ObjStandard,
  productSelected: Product
) => {
  const selected = selectedProduct(productSelected)
  const similarityOrder: ObjStandard = {}
  const comparisonObj: ComparisonObj = {
    product: {},
  }

  const similarityGroupSorted = Object.values(similarityGroup)
    ?.sort((a, b) => Number(a.prioridade) - Number(b.prioridade))
    ?.reduce((acc, cur) => {
      acc[cur?.originalTypeName as string] = {
        ...cur,
      }

      return acc
    }, {})

  for (const type in similarityGroupSorted) {
    if (similarityGroupSorted[type]?.prioridade !== '1') {
      const keyComparisonObjt = Object.keys(comparisonObj?.product)?.[0]

      if (
        comparisonObj.product[keyComparisonObjt]?.valor ===
        selected?.[0]?.similarity[keyComparisonObjt]?.valor
      ) {
        similarityOrder[type] = similarityGroupSorted[type]
      }

      comparisonObj.product[type] = similarityGroup[type]
    }

    comparisonObj.product[type] = similarityGroupSorted[type]
    similarityOrder[type] = similarityGroupSorted[type]
  }

  return similarityOrder
}

export const fixRecommendationsStructure = (
  recomendations: ProductRecommendation[],
  productSelected: Product
) => {
  const restructuredRecommendations: RestrucuredRecommendation[] = []

  for (const product of recomendations) {
    const similaritySpecGroup = product?.specificationGroups?.find(
      ({ name }) => normalize(name) === 'similaridade'
    )?.specifications

    if (!similaritySpecGroup) {
      continue
    }

    const similarityGroup: ObjStandard = {}

    similaritySpecGroup?.forEach((item) => {
      const lastUnderscoreIndex = item?.name?.lastIndexOf('_')
      const name = item?.name?.slice(lastUnderscoreIndex + 1)
      const key = item?.name?.slice(0, lastUnderscoreIndex)

      if (!similarityGroup[normalize(key)]) {
        similarityGroup[normalize(key)] = {
          originalTypeName: key,
        }
      }

      if (name?.toLowerCase() === 'valor') {
        similarityGroup[normalize(key)] = {
          ...similarityGroup[normalize(key)],
          originalValue: item?.values?.[0],
        }
      }

      similarityGroup[normalize(key)][normalize(name)] = normalize(
        item?.values?.[0]
      )
    })

    const similarity = sortingByPriority(similarityGroup, productSelected)

    const keys = Object.keys(similarity)?.length

    const finalProduct = {
      name: product?.productName,
      link: new URL(product?.link)?.pathname,
      similarity,
      keys,
    }

    restructuredRecommendations.push(finalProduct)
  }

  return !restructuredRecommendations?.[0]
    ? []
    : sortRecommendations(restructuredRecommendations)
}

const manipulationData = (
  type: string,
  mergedRecommendation: MergedRecommendation[],
  finalRecommendations: Record<string, unknown>
) => {
  const variants: Record<string, unknown> = {}

  for (const variation of mergedRecommendation) {
    const { name, link, selected, similarity } = variation

    const currentVariationOfType =
      normalize(similarity[normalize(type)]?.valor as string) ??
      normalize(similarity[type]?.valor as string) ??
      ''

    const typeSure = similarity[normalize(type)]?.tipo ?? similarity[type]?.tipo

    const similarWithoutImage = {
      name,
      link,
      selected,
      type: typeSure,
      originalTypeName:
        similarity[normalize(type)]?.originalTypeName ??
        similarity[type]?.originalTypeName ??
        type?.replaceAll('_', ' '),
      value: currentVariationOfType,
      originalValue:
        similarity[normalize(type)]?.originalValue ??
        similarity[type]?.originalValue ??
        currentVariationOfType,
    }

    const similarWithImage = {
      ...similarWithoutImage,
      imgLink: `http://${account}.vtexassets.com/arquivos/similaridade__${normalize(
        type
      )}__${currentVariationOfType}.png`,
    }

    if (!currentVariationOfType || currentVariationOfType === '') {
      continue
    }

    variants[
      selected ? `selected_${currentVariationOfType}` : currentVariationOfType
    ] =
      (normalize(String(typeSure)) === 'dropdown' && similarWithoutImage) ||
      (normalize(String(typeSure)) === 'imagem' && similarWithImage)
  }

  finalRecommendations[normalize(type)] = variants
}

export const transform = (
  recommendations: RestrucuredRecommendation[],
  productSelected: Product
) => {
  const variationTypes = Object.keys(recommendations?.[0]?.similarity)
  const selected = selectedProduct(productSelected)

  const selectedWithSelected = selected?.map((obj) => ({
    ...obj,
    selected: true,
  }))

  const recommendationsWithSelected = recommendations?.map((obj) => ({
    ...obj,
    selected: false,
  }))

  const mergedRecommendation = [
    ...selectedWithSelected,
    ...recommendationsWithSelected,
  ]

  const finalRecommendations: Record<string, unknown> = {}

  if (!variationTypes) {
    return null
  }

  for (const type of variationTypes) {
    manipulationData(type, mergedRecommendation, finalRecommendations)
  }

  return finalRecommendations
}

interface ProductRecommendation {
  productName: string
  link: string
  items: Items[]
  specificationGroups: SpecificationGroup[]
}
interface Items {
  images: Images[]
}
interface Images {
  imageUrl?: string
  ImageUrl?: string
}
interface SpecificationGroup {
  name: string
  specifications: Specification[]
}
interface Specification {
  name: string
  values: [string]
}
interface RestrucuredRecommendation {
  name: string
  link: string
  similarity: ObjStandard
  keys: number
}
interface FinalRecommendationsFallback {
  similar: Record<string, unknown>
}
interface ComparisonObj {
  product: ObjStandard
}
interface Product {
  ProductName: string
  DetailUrl: string
  Images: Images[]
  ProductSpecifications: Specifications[]
}
interface Specifications {
  FieldGroupName: string
  FieldName: string
  FieldValues: string[]
}

type ObjStandard = { [key: string]: Scalars['ObjectOrString'] }

interface MergedRecommendation {
  selected: boolean
  name: string
  link: string
  similarity: ObjStandard
}
